/**
 * ConversationForm — create and edit share this form, in place of the row or of the list
 * (UI-SPEC §2.20, mock `cvFormHTML`).
 *
 * « Créer » / « Enregistrer » is disabled until a persona and an intent are set, by the two
 * structural ceilings (cells before runs), and [B] by the configuration limit: the draft is
 * previewed (`POST .../conversations/preview`, debounced 400 ms after a change of runs, engines,
 * languages or places); `refused` disables it with the server's reason, a preview due, in flight
 * or failed disables it too. [A] never disabled for the balance (the save dialog shows it).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Dialog, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AllowanceFigures, Brand, BrandQuery, ConversationLoad, ConversationPersona, TargetZone, Tenant } from '@traaker/shared';
import { CONVERSATION_INTENT_ORDER, CONVERSATION_MAX_TAGS, CONVERSATION_MAX_TURNS, PROMPT_LANGUAGES, zoneDisplayLabel } from '@traaker/shared';
import { MONDIAL, limitReason, slotText } from '../promptStudioModel';
import type { LocKey } from '../promptStudioModel';
import { Pick } from '../Pick';
import { LimitReasonLine } from '../LimitReasonLine';
import { LocChip } from '../PromptRow';
import { languageName } from '../variantModel';
import { EngineLogo, ENGINE_META, engineKey } from '../../brand/EngineLogo';
import { Flag } from '../../../pages/dashboard/Flag';
import { languageFlag } from '../../../pages/dashboard/geoFlags';
import {
  CONV_RUNS,
  CONVERSATION_ZONE_TYPES,
  HOURS,
  MONDIAL_PLACE,
  TZS,
  confirmFigures,
  conversationCadence,
  convEngines,
  createPreviewer,
  formFromPersona,
  formMissing,
  isLegacyShape,
  isSaveableZone,
  isUnbounded,
  newForm,
  personaPayload,
  placeOfZone,
  placeOptions,
  saveErrorKey,
  saveErrorOf,
  scriptedTurns,
  structuralReason,
  toggleEngine,
  toggleLanguage,
  togglePlace,
  withMaxTurns,
} from './conversationModel';
import type { ConvForm, ConvPlace, PreviewState, SaveError } from './conversationModel';
import { conversationsApi, isAgencyCheck, savedPersona } from './conversationsApi';
import type { BrandPersonaListItem, SaveCheck } from './conversationsApi';
import { TurnScriptPicker } from './TurnScriptPicker';
import { ConversationSaveDialog } from './ConversationSaveDialog';

export interface ConversationFormProps {
  brand: Brand;
  tenant?: Tenant;
  /** null = a new conversation. */
  original: ConversationPersona | null;
  brandPersonas: BrandPersonaListItem[];
  queries: BrandQuery[];
  known: Map<LocKey, TargetZone>;
  load: ConversationLoad | null;
  /** Every tag of the brand's conversations (suggestions). */
  allTags: string[];
  onCancel: () => void;
  onSaved: (persona: ConversationPersona, load: ConversationLoad) => void;
  onRemoved: (personaId: string) => void;
  /** Reloads the list (a network failure or a conflict); the form waits for it. */
  onReload: () => unknown;
  onManagePersonas: () => void;
}

const sectionTitle = { fontSize: 12.5, fontWeight: 700, borderTop: '1px solid', borderColor: 'divider', pt: 1.25, mt: 1.25, mb: 0.75 };
const label = { width: 156, minWidth: 156, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, color: 'text.secondary', letterSpacing: '.03em' };
const amberLine = { fontSize: 11.5, color: '#b45309', mt: 0.5 };
const greyLine = { fontSize: 11.5, color: 'text.secondary', mt: 0.5 };

export function ConversationForm({
  brand,
  tenant,
  original,
  brandPersonas,
  queries,
  known,
  load,
  allTags,
  onCancel,
  onSaved,
  onRemoved,
  onReload,
  onManagePersonas,
}: ConversationFormProps) {
  const { t, i18n } = useTranslation();
  const tier = tenant?.tier ?? 'basic';
  const isAgency = tier === 'agency';
  const [form, setForm] = useState<ConvForm>(() => (original ? formFromPersona(original, brand, tier) : newForm(brand, tier)));
  const [pick, setPick] = useState<'persona' | 'place' | 'language' | null>(null);
  const [pickQ, setPickQ] = useState('');
  const [turnOpen, setTurnOpen] = useState<number | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [tagDraft, setTagDraft] = useState('');
  const [preview, setPreview] = useState<PreviewState<SaveCheck>>({ status: 'idle' });
  // A 409 ALLOWANCE_EXCEEDED on the save greys the button until a preview that is not refused.
  const [refusedBySave, setRefusedBySave] = useState<AllowanceFigures | null>(null);
  const [line, setLine] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // After a failure with no HTTP status the list is reloaded BEFORE another save is possible (H3).
  const [reloading, setReloading] = useState(false);
  // Search results picked in this session (they have no entry in the known zones).
  const [picked, setPicked] = useState<ConvPlace[]>([]);

  const persona = brandPersonas.find((p) => p.brandPersonaId === form.brandPersonaId) ?? null;
  const personaName = persona?.name ?? original?.name ?? '';
  const engines = convEngines(tier);
  const cadence = conversationCadence(load, tier, original);
  const figures = confirmFigures(form, cadence);
  const structural = structuralReason(figures);
  // A link to a persona deleted since (not in the list) is no persona: the save would 400.
  const missing = formMissing({ brandPersonaId: persona ? form.brandPersonaId : null, intentId: form.intentId });
  const unbounded = isUnbounded(load?.figures, isAgency);

  // ---- [B] the configuration-limit preview (U15) ----
  const previewer = useRef(createPreviewer<Record<string, unknown>, SaveCheck>(
    (body) => conversationsApi.preview(brand.brandId, body),
    (s) => {
      setPreview(s);
      if (s.status === 'done' && !isAgencyCheck(s.result) && !s.result.refused) setRefusedBySave(null);
    },
  ));
  useEffect(() => () => previewer.current.cancel(), []);
  const needKey = JSON.stringify([form.engines, form.runs, form.languages, form.places.map((p) => p.key), !missing]);
  const body = () => {
    const b = personaPayload(form, original, isAgency, personaName);
    return original ? { ...b, personaId: original.personaId } : b;
  };
  useEffect(() => {
    if (isAgency || missing || structural) {
      previewer.current.cancel();
      setPreview({ status: 'idle' });
      return;
    }
    previewer.current.schedule(body());
  }, [needKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const refusedFigures: AllowanceFigures | null =
    refusedBySave ?? (preview.status === 'done' && !isAgencyCheck(preview.result) && preview.result.refused ? preview.result.figuresAfter : null);
  const limitSlot = refusedFigures ? limitReason(refusedFigures, 'conversationRuns') : null;
  const previewBlocks = !isAgency && !missing && !structural && (preview.status === 'pending' || preview.status === 'failed');
  const disabledTitle =
    missing === 'persona' ? t('promptStudio.conv.missingPersona')
      : missing === 'intent' ? t('promptStudio.conv.missingIntent')
        : structural === 'maxCells' ? t('promptStudio.conv.maxCells')
          : structural === 'maxRuns' ? t('promptStudio.save.maxRuns')
            : refusedFigures ? (limitSlot ? slotText(t, limitSlot) : '')
              : previewBlocks ? (preview.status === 'failed' ? t('promptStudio.fit.previewFailed') : '')
                : '';
  const saveDisabled = busy || reloading || !!missing || !!structural || !!refusedFigures || previewBlocks;
  const lastPreview = preview.status === 'done' && !isAgencyCheck(preview.result) ? preview.result.figuresAfter : null;
  const reload = async () => {
    setReloading(true);
    try {
      await Promise.resolve(onReload());
    } finally {
      setReloading(false);
    }
  };

  // ---- Options of the pickers ----
  const ownPlaces = useMemo(
    () => (original ? formFromPersona(original, brand, tier).places.filter((p) => p.zone) : []),
    [original], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const places = useMemo(() => {
    const out = placeOptions([...ownPlaces, ...picked], known);
    for (const p of form.places) if (p.zone && !out.some((o) => o.key === p.key)) out.unshift(p);
    return out;
  }, [ownPlaces, picked, known, form.places]);
  const languageOptions = useMemo(
    () => [...PROMPT_LANGUAGES]
      .map(({ code }) => ({
        code,
        label: languageName(code, i18n.language),
        icon: languageFlag(code) ? <Flag countryCode={languageFlag(code)} size={18} /> : undefined,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, i18n.language)),
    [i18n.language],
  );

  const set = (patch: Partial<ConvForm>) => { setLine(null); setForm((f) => ({ ...f, ...patch })); };
  const togglePlaceKey = (key: string) => {
    const p = key === MONDIAL ? MONDIAL_PLACE : places.find((o) => o.key === key);
    if (p) set({ places: togglePlace(form.places, p) });
  };
  const pickZone = (z: TargetZone) => {
    // A search result the save would refuse (a locality or village, no coordinates): its own line, never a 400.
    if (!CONVERSATION_ZONE_TYPES.includes(z.type) || !isSaveableZone(z)) {
      setLine(t('promptStudio.conv.placeUnsupported'));
      return;
    }
    const p = placeOfZone(z);
    setPicked((xs) => (xs.some((x) => x.key === p.key) ? xs : [...xs, p]));
    set({ places: togglePlace(form.places, p) });
  };
  const addTag = () => {
    const tag = tagDraft.trim();
    if (tag && !form.tags.includes(tag) && form.tags.length < CONVERSATION_MAX_TAGS) set({ tags: [...form.tags, tag] });
    setTagDraft('');
  };

  const handleError = (err: unknown) => applyError(saveErrorOf(err));
  const applyError = (e: SaveError) => {
    if (e.kind === 'refused') { setRefusedBySave(e.figuresAfter); return; }
    if (e.kind === 'network' || e.kind === 'conflict') void reload();
    setLine(e.kind === 'invalid' ? e.message : t(saveErrorKey(e) ?? 'promptStudio.common.saveFailed'));
  };

  // « Désactiver » lowers the need: never previewed (UI-SPEC §2.20).
  const handleDisable = async () => {
    if (!original || busy) return;
    setBusy(true);
    try {
      const r = await conversationsApi.update(brand.brandId, original.personaId, { status: 'paused' });
      onSaved(savedPersona(r), r.load);
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  };
  const handleDelete = async () => {
    if (!original || busy) return;
    setBusy(true);
    try {
      await conversationsApi.remove(brand.brandId, original.personaId);
      setDeleteOpen(false);
      onRemoved(original.personaId);
    } catch (err) {
      setDeleteOpen(false);
      handleError(err);
    } finally {
      setBusy(false);
    }
  };

  // ---- Tours [A] note ----
  const scriptedCount = scriptedTurns(form.items).filter((n) => n <= form.maxTurns).length;
  // Rendered with {{n}} empty (« tours, … », UI-SPEC §4.10 note).
  const turnsNote = (scriptedCount === 0 ? t('promptStudio.conv.turnsGenerated', { n: '' })
    : scriptedCount >= form.maxTurns ? t('promptStudio.conv.turnsAll', { n: '' })
      : t('promptStudio.conv.turnsSome', { n: '', count: scriptedCount })).trim();
  const maxTurns = isAgency ? form.maxTurns : CONVERSATION_MAX_TURNS;
  const freeTurn = Array.from({ length: maxTurns }, (_, i) => i + 1).find((n) => !scriptedTurns(form.items).includes(n));

  const square = (on: boolean) => ({
    minWidth: 28, height: 28, px: 0.75, borderRadius: 1, border: '1px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    borderColor: on ? '#7c3aed' : 'divider', bgcolor: on ? '#7c3aed' : 'background.paper', color: on ? '#fff' : 'text.primary',
  });
  const chip = (on: boolean) => ({
    fontSize: 12.5, border: '1px solid', borderRadius: 999, px: 1.25, py: 0.4, cursor: 'pointer',
    borderColor: on ? '#7c3aed' : 'divider', bgcolor: on ? 'rgba(124,58,237,.08)' : 'transparent', color: on ? '#5b21b6' : 'text.primary',
  });
  const intentLabel = (id: string) => t(`conversations.intent${id.charAt(0).toUpperCase()}${id.slice(1)}`);

  return (
    <Box sx={{ px: 2.25, pt: 1.5, pb: 2 }} onClick={(e) => e.stopPropagation()}>
      {/* PERSONA */}
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 30, gap: 1 }}>
        <Box sx={label}>{t('promptStudio.conv.persona')}</Box>
        <Box component="button" onClick={() => { setPickQ(''); setPick('persona'); }}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, maxWidth: 520, minWidth: 0, fontSize: 13, px: 1.25, py: 0.5, borderRadius: 1.5, cursor: 'pointer',
            bgcolor: 'background.paper', border: persona ? '1px solid' : '1px dashed', borderColor: persona ? 'divider' : 'rgba(124,58,237,.45)', color: persona ? 'text.primary' : '#7c3aed' }}>
          {persona ? (
            <>
              <span>👤 {persona.name}</span>
              {persona.description && (
                <Box component="span" sx={{ color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>· {persona.description}</Box>
              )}
              <span>⌄</span>
            </>
          ) : t('promptStudio.conv.choosePersona')}
        </Box>
        <Box component="button" onClick={onManagePersonas} title={t('promptStudio.conv.managePersonasTitle')}
          sx={{ fontSize: 12, color: '#7c3aed', border: 'none', bgcolor: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          {t('promptStudio.conv.managePersonas')}
        </Box>
      </Box>
      {!persona && <Box sx={{ ...greyLine, ml: '164px' }}>{t('promptStudio.conv.personaRequired')}</Box>}

      {/* Déroulé de la conversation */}
      <Box sx={sectionTitle}>{t('promptStudio.conv.flow')}</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 30, gap: 1 }}>
        <Box sx={label}>
          {t('promptStudio.conv.intent')}{' '}
          <Tooltip title={t('promptStudio.conv.intentInfo')}><span>ⓘ</span></Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {CONVERSATION_INTENT_ORDER.map((id) => (
            <Box key={id} component="button" onClick={() => set({ intentId: id })} sx={chip(form.intentId === id)}>{intentLabel(id)}</Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 30, gap: 1, mt: 0.5 }}>
        <Box sx={label}>{t('promptStudio.conv.turns')}</Box>
        {isAgency && (
          <>
            <Box sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>{t('promptStudio.conv.ceiling')}</Box>
            {Array.from({ length: CONVERSATION_MAX_TURNS }, (_, i) => i + 1).map((n) => (
              <Box key={n} component="button" title={t('promptStudio.conv.ceilingTitle', { t: n })}
                onClick={() => { setForm((f) => withMaxTurns(f, n)); if (turnOpen && turnOpen > n) setTurnOpen(null); }} sx={square(form.maxTurns === n)}>
                {n}
              </Box>
            ))}
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{turnsNote}</Box>
          </>
        )}
        {freeTurn !== undefined && turnOpen === null && (
          <Box component="button" onClick={() => setTurnOpen(freeTurn)}
            sx={{ fontSize: 12.5, color: '#7c3aed', border: 'none', bgcolor: 'transparent', cursor: 'pointer' }}>
            {t('promptStudio.conv.scriptTurn')}
          </Box>
        )}
      </Box>
      {/* One line per scripted turn */}
      {scriptedTurns(form.items).filter((n) => n <= maxTurns).map((n) => {
        const items = form.items.filter((i) => i.turn === n);
        return (
          <Box key={n} onClick={() => setTurnOpen(n)} title={t('promptStudio.conv.turnTitle')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: '164px', mt: 0.5, pl: 1.25, borderLeft: '2px solid', borderColor: n === turnOpen ? '#7c3aed' : 'divider', cursor: 'pointer', minHeight: 28 }}>
            <Box sx={{ width: 44, fontSize: 12, fontWeight: 600 }}>{t('promptStudio.conv.turnN', { n })}</Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
              {items.map((i) => (
                <Box key={i.text} sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center', fontSize: 12, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 0.75, py: 0.2, maxWidth: 300 }}>
                  {i.source !== 'prompt' && (
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {t(i.source === 'objection' ? 'promptStudio.conv.kind.ob' : i.source === 'question' ? 'promptStudio.conv.kind.qa' : 'promptStudio.conv.kind.tx')}
                    </Box>
                  )}
                  <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.text}</Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ fontSize: 11.5, color: 'text.secondary', whiteSpace: 'nowrap' }}>
              {items.length === 1 ? t('promptStudio.conv.usedAsIs') : t('promptStudio.conv.bestAtRun')}
            </Box>
            <Box component="button" title={t('promptStudio.conv.removeScript')}
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); set({ items: form.items.filter((i) => i.turn !== n) }); if (turnOpen === n) setTurnOpen(null); }}
              sx={{ border: 'none', bgcolor: 'transparent', cursor: 'pointer', color: 'text.secondary' }}>✕</Box>
          </Box>
        );
      })}
      {turnOpen !== null && (
        <Box sx={{ ml: '164px' }}>
          <TurnScriptPicker
            turn={turnOpen}
            maxTurns={maxTurns}
            items={form.items}
            intentId={form.intentId}
            queries={queries}
            personaObjections={persona?.objections}
            brandObjections={brand.strategy?.customerObjections}
            customerQuestions={brand.strategy?.customerQuestions}
            onTurn={setTurnOpen}
            onItems={(items) => set({ items })}
            onDone={() => setTurnOpen(null)}
          />
        </Box>
      )}
      {form.keptStartingPrompt !== undefined && <Box sx={{ ...greyLine, ml: '164px' }}>{t('promptStudio.conv.keptStart')}</Box>}

      {/* Mesure */}
      <Box sx={sectionTitle}>{t('promptStudio.conv.measure')}</Box>
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 30, gap: 1 }}>
        <Box sx={label}>{t('promptStudio.conv.engines')}</Box>
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          {engines.map((e) => {
            const on = form.engines.includes(e);
            return (
              <Tooltip key={e} title={ENGINE_META[engineKey(e)].label}>
                <Box component="button" onClick={() => set({ engines: toggleEngine(form.engines, e) })}
                  sx={{ width: 36, height: 32, borderRadius: 2, border: '1px solid', borderColor: on ? 'rgba(124,58,237,.35)' : 'divider', bgcolor: 'background.paper',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', filter: on ? 'none' : 'grayscale(1)', opacity: on ? 1 : 0.34, p: 0 }}>
                  <EngineLogo engine={e} size={18} />
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 30, gap: 1, mt: 0.5 }}>
        <Box sx={label}>Runs</Box>
        {CONV_RUNS.map((n) => (
          <Box key={n} component="button" onClick={() => set({ runs: n })} sx={square(form.runs === n)}>{n}</Box>
        ))}
        <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{t('promptStudio.conv.runsNote')}</Box>
      </Box>
      {original && isLegacyShape(original) && <Box sx={{ ...greyLine, ml: '164px' }}>{t('promptStudio.conv.legacyShape')}</Box>}

      {/* Plus d'options */}
      {moreOpen && (
        <Box sx={{ borderTop: '1px dashed', borderColor: 'divider', mt: 1.25, pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 30, gap: 1 }}>
            <Box sx={label}>{t('promptStudio.conv.hour')}</Box>
            <Box component="select" value={form.scheduleHour} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => set({ scheduleHour: e.target.value })}
              sx={{ fontSize: 13, fontFamily: 'inherit', px: 0.5, py: 0.25 }}>
              {(HOURS.includes(form.scheduleHour) ? HOURS : [form.scheduleHour, ...HOURS]).map((h) => <option key={h} value={h}>{h}</option>)}
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{t(isAgency ? 'promptStudio.conv.hourNoteAgency' : 'promptStudio.conv.hourNote')}</Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 30, gap: 1 }}>
            <Box sx={label}>{t('promptStudio.conv.tz')}</Box>
            <Box component="select" value={form.timezone} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => set({ timezone: e.target.value })}
              sx={{ fontSize: 13, fontFamily: 'inherit', px: 0.5, py: 0.25 }}>
              {!TZS.some((z) => z.tz === form.timezone) && <option value={form.timezone}>{form.timezone}</option>}
              {TZS.map((z) => <option key={z.tz} value={z.tz}>{`${z.tz} · ${z.country}`}</option>)}
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{t('promptStudio.conv.tzNote')}</Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 30, gap: 1, flexWrap: 'wrap' }}>
            <Box sx={label}>Tags</Box>
            {form.tags.map((tag) => (
              <Box key={tag} sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center', fontSize: 12, border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 1, py: 0.2 }}>
                {tag}
                <Box component="button" onClick={() => set({ tags: form.tags.filter((x) => x !== tag) })}
                  sx={{ border: 'none', bgcolor: 'transparent', cursor: 'pointer', p: 0, color: 'text.secondary' }}>✕</Box>
              </Box>
            ))}
            <Box component="input" list="conv-tag-suggestions" value={tagDraft} placeholder={t('promptStudio.conv.addTag')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTagDraft(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } }}
              onBlur={addTag}
              sx={{ fontSize: 12.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.4, outline: 'none', fontFamily: 'inherit', minWidth: 140 }} />
            <datalist id="conv-tag-suggestions">
              {allTags.filter((x) => !form.tags.includes(x)).map((x) => <option key={x} value={x} />)}
            </datalist>
          </Box>
        </Box>
      )}

      {/* Action row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
        <Box component="button" onClick={() => { setPickQ(''); setPick('place'); }}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper', px: 0.75, py: 0.25, cursor: 'pointer' }}>
          <LocChip zone={form.places[0]?.zone ?? null} />
          {form.places.length > 1 && <Box component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>+{form.places.length - 1}</Box>}
        </Box>
        <Box component="button" onClick={() => { setPickQ(''); setPick('language'); }}
          title={form.languages.map((c) => languageName(c, i18n.language)).join(', ')}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.paper', px: 0.75, py: 0.5, cursor: 'pointer', fontSize: 12 }}>
          {form.languages.map((code) =>
            languageFlag(code) ? <Flag key={code} countryCode={languageFlag(code)} size={18} /> : <span key={code}>{code.toUpperCase()}</span>,
          )}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box component="button" onClick={() => setMoreOpen((o) => !o)}
          sx={{ fontSize: 12.5, color: 'text.secondary', border: 'none', bgcolor: 'transparent', cursor: 'pointer' }}>
          {t('promptStudio.conv.moreOptions')} {moreOpen ? '⌃' : '⌄'}
        </Box>
        {original && (
          <>
            <Button size="small" variant="text" disabled={busy} onClick={() => void handleDisable()} sx={{ color: 'text.secondary' }}>{t('promptStudio.edit.disable')}</Button>
            <Button size="small" variant="text" color="error" disabled={busy} onClick={() => setDeleteOpen(true)}>{t('promptStudio.conv.delete')}</Button>
          </>
        )}
        <Button size="small" variant="text" onClick={onCancel}>{t('common.cancel')}</Button>
        <Tooltip title={disabledTitle}>
          <span>
            <Button size="small" variant="contained" disabled={saveDisabled} sx={{ fontWeight: 600 }}
              onClick={() => { setLine(null); setConfirmOpen(true); }}>
              {original ? t('common.save') : t('promptStudio.conv.create')}
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Lines under the action row: structural first, then the server's reason */}
      {structural && <Box sx={amberLine}>{t(structural === 'maxCells' ? 'promptStudio.conv.maxCells' : 'promptStudio.save.maxRuns')}</Box>}
      {!missing && !structural && limitSlot && <LimitReasonLine reason={limitSlot} />}
      {!missing && !structural && !refusedFigures && preview.status === 'failed' && <Box sx={amberLine}>{t('promptStudio.fit.previewFailed')}</Box>}
      {unbounded && <Box sx={amberLine}>{t('promptStudio.conv.unbounded')}</Box>}
      {line && <Box sx={amberLine}>{line}</Box>}
      {form.places.some((p) => p.key === MONDIAL) && <Box sx={greyLine}>{t('conversations.zoneWorldNote')}</Box>}

      {/* Pickers */}
      <Pick
        open={pick === 'persona'}
        kind="persona"
        selected={form.brandPersonaId ?? ''}
        personas={brandPersonas}
        searchQuery={pickQ}
        onSearchChange={setPickQ}
        searchPlaceholder={t('promptStudio.conv.personaSearch')}
        onToggle={(id) => { set({ brandPersonaId: id || null }); setPick(null); }}
        onClose={() => setPick(null)}
      />
      <Pick
        open={pick === 'place'}
        kind="place"
        multi
        selected={form.places.map((p) => p.key)}
        placeOptions={places.map((p) => ({ key: p.key, zone: p.zone, label: p.zone ? zoneDisplayLabel(p.zone) : t('conversations.zoneWorld') }))}
        searchQuery={pickQ}
        onSearchChange={setPickQ}
        lang={(brand.strategy?.workingLanguage || 'fr').toLowerCase()}
        onToggle={togglePlaceKey}
        onPickZone={pickZone}
        onClose={() => setPick(null)}
      />
      <Pick
        open={pick === 'language'}
        kind="language"
        multi
        selected={form.languages}
        languageOptions={languageOptions}
        searchQuery={pickQ}
        onSearchChange={setPickQ}
        onToggle={(code) => set({ languages: toggleLanguage(form.languages, code) })}
        onClose={() => setPick(null)}
      />

      {confirmOpen && (
        <ConversationSaveDialog
          brandId={brand.brandId}
          original={original}
          isAgency={isAgency}
          body={personaPayload(form, original, isAgency, personaName)}
          figures={figures}
          cadence={cadence}
          maxTurns={form.maxTurns}
          lastPreview={lastPreview}
          onClose={() => setConfirmOpen(false)}
          onSaved={(p, l) => { setConfirmOpen(false); onSaved(p, l); }}
          onRefused={(fa) => { setConfirmOpen(false); setRefusedBySave(fa); }}
          onUncertain={(e) => { setConfirmOpen(false); applyError(e); }}
        />
      )}

      {/* « Supprimer » confirmation (C16) */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} PaperProps={{ sx: { width: 440, borderRadius: 1.5, p: 3 } }}>
        <Box sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>{t('promptStudio.conv.deleteTitle')}</Box>
        <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>{t('promptStudio.conv.deleteBody')}</Box>
        <Box sx={{ fontSize: 12.5, color: 'text.secondary' }}>{t('promptStudio.conv.deleteHint')}</Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button size="small" onClick={() => setDeleteOpen(false)}>{t('common.cancel')}</Button>
          <Button size="small" variant="contained" color="error" disabled={busy} onClick={() => void handleDelete()}>{t('promptStudio.conv.delete')}</Button>
        </Box>
      </Dialog>
    </Box>
  );
}
